import React, { useEffect, useState } from "react";
import { SimpleViewModal } from "./SimpleViewModal";
import { Input } from "../Input";
import { useMusicApi } from "../../api/useMusicApi";
import { useLoading } from "../../hooks/use-loading";
import { useTranslation } from "../../hooks/useTranslation";
import { ValidationInputWrapper } from "../ValidationInputWrapper";
import { useValidationErrors } from "../../hooks/use-validation-errors";
import { useAdvancedState } from "../../hooks/use-advanced-state";
import { AxiosError } from "axios";
import cn from "classnames";
import { InputLabel } from "../InputLabel";
import { toast } from "react-toastify";
import { Preloader } from "../Preloader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";

export type CreateArtistPayload = {
  canonical_name: string;
  vk_url?: string;
}

type Props = {
  onClose: () => void;
}
export const CreateArtistModal: React.FC<Props> = ({ onClose }) => {
  const { postArtistCreate, getCreatedArtists } = useMusicApi();
  const { t } = useTranslation();
  const [createArtistForm, setArtistForm] = useAdvancedState<CreateArtistPayload>({});
  const [createdArtists, setCreatedArtists] = useState<CreateArtistPayload[]>([]);
  const { loadings, startLoading, stopLoading } = useLoading({
    fetchArtists: true,
    createArtist: false
  });

  const { validationErrors, setValidationErrors } = useValidationErrors<CreateArtistPayload>(createArtistForm);

  useEffect(() => {
    startLoading("fetchArtists");
    void getCreatedArtists().then(r => {
      setCreatedArtists(r as any);
    }).finally(() => stopLoading("fetchArtists"));
  }, []);

  const handleSuccessClick = async () => {
    try {
      startLoading("createArtist");
      const createdArtist = await postArtistCreate(createArtistForm as any);
      toast.success(t("create_artist_modal.success_created"));
      setCreatedArtists(prevState => [...prevState, createdArtist]);
      setArtistForm({ canonical_name: "" });
    } catch (e) {
      setValidationErrors(
        ((e as AxiosError).response?.data as any)?.errors ?? [],
        createArtistForm
      );
    } finally {
      stopLoading("createArtist");
    }
  };

  return <SimpleViewModal
    title={t("create_artist_modal.title")}
    className={cn("w-8/12 xl:w-6/12 2xl:w-5/12")}
    onSuccess={handleSuccessClick}
    isSuccessLoading={loadings.createArtist}
    disableSuccess={loadings.createArtist}
    onCancel={onClose}>
    <div className={"rounded border p-2 my-3"}>
      {t("create_artist_modal.notice")}
    </div>
    <ValidationInputWrapper attribute={"canonical_name"} validationErrors={validationErrors}>
      <Input
        validationError={
          validationErrors?.find((e) => e.attr === "canonical_name")
            ?.detail
        }
        label={t("create_artist_modal.canonical_name").toString()}
        value={createArtistForm?.canonical_name ?? ""}
        onChange={(value) => setArtistForm({ canonical_name: value })}
      />
    </ValidationInputWrapper>

    {loadings.fetchArtists && <Preloader size={"2x"} className={"my-3"} />}

    {
      createdArtists?.length > 0 && <div className={"mt-3 w-full"}>
        <InputLabel
          label={t("create_artist_modal.recently_created").toString()}
          labelHint={t("create_artist_modal.recently_created_hint").toString()}
          className={{ label: "mb-2" }}
        />
        {createdArtists?.map(a => {
          return <div className={"flex justify-between w-full py-2 px-1 odd:bg-gray-200"} key={a.vk_url}>
            <div className={"mb-1 w-[350px]"}>
              <FontAwesomeIcon icon={faUser} className={"mr-1"} />
              {a.canonical_name}
            </div>
            <div className={"flex-1"}><a href={a.vk_url} target={"_blank"}>{a.vk_url}</a>
            </div>
          </div>;
        })}
      </div>
    }
  </SimpleViewModal>;
};
